import type {
  CreateReferenceTermInput,
  MemberRole,
  ReferenceKind,
  ReferenceTermView,
  AdminMemberView,
  AuthenticatedMember,
  BlindProgressView,
  CreateRumCommentInput,
  CreateRumInput,
  CreateRumLinkInput,
  GuessView,
  RumCommentView,
  RumLinkView,
  MemberStatsView,
  MemberView,
  Page,
  IssuedInvitationView,
  PendingInvitationView,
  RevealResultView,
  RumDetailView,
  RumTastingsView,
  RumView,
  SearchRumsInput,
  SessionRecapView,
  SessionView,
  Settings,
  TastingView,
  UpdateRumInput,
  UpsertGuessInput,
  UpsertTastingInput,
} from '@cercle/shared';
import type { HttpClient } from '../http/http-client';

/** Le plafond de `paginationSchema` côté API. */
const MAX_PER_PAGE = 100;

export interface PalatePreference {
  label: string;
  averageGiven: number;
  clubAverage: number;
  count: number;
}

/**
 * Surface de l'API vue par le front, en un seul objet typé par les contrats partagés.
 *
 * Une seule couche de traduction : les vues de `@cercle/shared` sont directement les types
 * manipulés par les écrans. Recopier ces formes en « modèles de domaine front » n'apporterait
 * rien ici — il n'y a pas de logique métier côté client à protéger, elle est toute côté API.
 */
export function createApi(http: HttpClient) {
  return {
    auth: {
      signIn: (email: string, password: string) =>
        http.post<AuthenticatedMember>('/auth/login', { email, password }),
      changePassword: (input: { currentPassword?: string; newPassword: string }) =>
        http.post<void>('/auth/password', input),
      requestMagicLink: (email: string) => http.post<{ accepted: true }>('/auth/magic-link', { email }),
      consumeMagicLink: (token: string) =>
        http.post<AuthenticatedMember>('/auth/magic-link/consume', { token }),
      describeInvitation: (token: string) =>
        http.get<{ email: string; invitedByPseudo: string | null }>(`/auth/invitations/${token}`),
      acceptInvitation: (input: {
        token: string;
        pseudo: string;
        password: string;
        avatarUrl?: string | null;
      }) => http.post<AuthenticatedMember>('/auth/invitations/accept', input),
      pseudoAvailable: (pseudo: string) =>
        http.get<{ available: boolean }>(`/auth/pseudo-available?pseudo=${encodeURIComponent(pseudo)}`),
      me: () => http.get<AuthenticatedMember>('/auth/me'),
      logout: () => http.post<void>('/auth/logout'),
    },

    members: {
      list: () => http.get<MemberView[]>('/members'),
      updateProfile: (input: { pseudo?: string; avatarUrl?: string | null }) =>
        http.patch<MemberView>('/members/me', input),
      // Même forme que la photo de bouteille : le navigateur pose la frontière multipart
      // et le type de contenu, les fixer à la main envoie un corps silencieusement vide.
      setAvatar: (file: File) => {
        const form = new FormData();
        form.append('photo', file);
        return http.postForm<MemberView>('/members/me/avatar', form);
      },
      removeAvatar: () => http.delete<MemberView>('/members/me/avatar'),
      stats: () => http.get<MemberStatsView>('/members/me/stats'),
      palate: () =>
        http.get<{ byCountry: PalatePreference[]; byType: PalatePreference[] }>('/members/me/palate'),
    },

    reference: {
      list: (kind: ReferenceKind) =>
        http.get<ReferenceTermView[]>(`/reference?kind=${kind}`),
      add: (input: CreateReferenceTermInput) =>
        http.post<ReferenceTermView>('/reference', input),
      rename: (id: string, label: string) =>
        http.patch<ReferenceTermView>(`/reference/${id}`, { label }),
      merge: (id: string, intoId: string) =>
        http.post<ReferenceTermView>(`/reference/${id}/merge`, { intoId }),
    },

    rums: {
      search: (criteria: Partial<SearchRumsInput> = {}) =>
        http.get<Page<RumView>>(`/rums${toQuery(criteria)}`),
      // Toute la sélection, page après page : un sélecteur qui s'arrête à la 24ᵉ bouteille
      // cache les autres sans le dire.
      searchAll: async (criteria: Partial<SearchRumsInput> = {}) => {
        const items: RumView[] = [];
        for (let page = 1; ; page += 1) {
          const batch = await http.get<Page<RumView>>(
            `/rums${toQuery({ ...criteria, page, perPage: MAX_PER_PAGE })}`,
          );
          items.push(...batch.items);
          if (batch.items.length === 0 || items.length >= batch.total) return items;
        }
      },
      detail:(id: string) => http.get<RumDetailView>(`/rums/${id}`),
      // Les pays réellement en cave, les plus représentés d'abord. Calculés par l'API :
      // c'est elle qui sait quelles bouteilles sont encore masquées.
      countries: () => http.get<string[]>('/rums/countries'),
      create: (input: CreateRumInput) => http.post<RumView>('/rums', input),
      update: (id: string, input: UpdateRumInput) => http.patch<RumView>(`/rums/${id}`, input),
      // Multipart : on laisse le navigateur poser la frontière et le type de contenu.
      // Les fixer à la main est la première cause d'envoi silencieusement vide.
      setPhoto: (id: string, file: File) => {
        const form = new FormData();
        form.append('photo', file);
        return http.postForm<RumView>(`/rums/${id}/photo`, form);
      },
      removePhoto: (id: string) => http.delete<RumView>(`/rums/${id}/photo`),
      tastings: (rumId: string, sessionId: string) =>
        http.get<RumTastingsView>(`/rums/${rumId}/tastings?sessionId=${sessionId}`),

      // Liens utiles et fil de discussion : ouverts à tout membre, à tout moment. Contrairement
      // aux notes, ces écritures ne passent pas par la file offline — elles n'ont pas de clé
      // métier qui rendrait un rejeu inoffensif, et rien n'y presse en séance.
      links: (rumId: string) => http.get<RumLinkView[]>(`/rums/${rumId}/links`),
      addLink: (rumId: string, input: CreateRumLinkInput) =>
        http.post<RumLinkView>(`/rums/${rumId}/links`, input),
      removeLink: (rumId: string, linkId: string) =>
        http.delete<void>(`/rums/${rumId}/links/${linkId}`),

      comments: (rumId: string) => http.get<RumCommentView[]>(`/rums/${rumId}/comments`),
      addComment: (rumId: string, input: CreateRumCommentInput) =>
        http.post<RumCommentView>(`/rums/${rumId}/comments`, input),
      removeComment: (rumId: string, commentId: string) =>
        http.delete<void>(`/rums/${rumId}/comments/${commentId}`),
    },

    sessions: {
      next: () => http.get<SessionView | null>('/sessions/next'),
      list: () => http.get<SessionView[]>('/sessions'),
      byId: (id: string) => http.get<SessionView>(`/sessions/${id}`),
      recap: (id: string) => http.get<SessionRecapView>(`/sessions/${id}/recap`),
      // « J'apporte une bouteille » : hors régie, pour un membre inscrit à la séance.
      contributeRum: (id: string, rumId: string, isBlind = false) =>
        http.post<SessionView>(`/sessions/${id}/rums`, { rumId, isBlind }),
      setAttendance: (id: string, input: { confirmed: boolean; guestCount: number }) =>
        http.put<SessionView>(`/sessions/${id}/attendance`, input),
    },

    tastings: {
      upsert: (input: UpsertTastingInput) => http.put<TastingView>('/tastings', input),
      mine: (sessionId: string) => http.get<TastingView[]>(`/tastings/mine?sessionId=${sessionId}`),
    },

    guesses: {
      upsert: (input: UpsertGuessInput) => http.put<GuessView>('/guesses', input),
      mine: (sessionId: string, rumId: string) =>
        http.get<GuessView | null>(`/guesses/mine?sessionId=${sessionId}&rumId=${rumId}`),
      progress: (sessionId: string, rumId: string) =>
        http.get<BlindProgressView>(`/guesses/progress?sessionId=${sessionId}&rumId=${rumId}`),
    },

    settings: {
      read: () => http.get<Settings>('/settings'),
    },

    admin: {
      members: () => http.get<AdminMemberView[]>('/admin/members'),
      // Émettre rend le lien en clair ; le lire ne le rend pas. D'où deux types.
      invite: (email: string) =>
        http.post<IssuedInvitationView>('/admin/members/invite', { email }),
      disableMember: (id: string) => http.post<MemberView>(`/admin/members/${id}/disable`),
      enableMember: (id: string) => http.post<MemberView>(`/admin/members/${id}/enable`),
      deleteMember: (id: string) => http.delete<void>(`/admin/members/${id}`),
      invitations: () => http.get<PendingInvitationView[]>('/admin/invitations'),
      resendInvitation: (id: string) =>
        http.post<IssuedInvitationView>(`/admin/invitations/${id}/resend`),
      revokeInvitation: (id: string) => http.delete<void>(`/admin/invitations/${id}`),

      createSession: (input: { startsAt: string; endsAt?: string | null; location?: string | null }) =>
        http.post<SessionView>('/admin/sessions', input),
      updateSession: (
        id: string,
        input: { startsAt?: string; endsAt?: string | null; location?: string | null },
      ) => http.patch<SessionView>(`/admin/sessions/${id}`, input),
      attachRum: (id: string, input: { rumId: string; orderIndex: number; isBlind: boolean }) =>
        http.post<SessionView>(`/admin/sessions/${id}/rums`, input),
      detachRum: (id: string, rumId: string) =>
        http.delete<SessionView>(`/admin/sessions/${id}/rums/${rumId}`),
      setBlind: (id: string, rumId: string, isBlind: boolean) =>
        http.patch<SessionView>(`/admin/sessions/${id}/rums/${rumId}/blind`, { isBlind }),
      openSession: (id: string) => http.post<SessionView>(`/admin/sessions/${id}/open`),
      reveal: (id: string, rumId: string) =>
        http.post<RevealResultView>(`/admin/sessions/${id}/rums/${rumId}/reveal`),
      closeSession: (id: string) => http.post<SessionView>(`/admin/sessions/${id}/close`),
      // La liste complète dans l'ordre voulu, et non un déplacement : l'appel est rejouable.
      reorderRums: (id: string, rumIds: string[]) =>
        http.patch<SessionView>(`/admin/sessions/${id}/rums/order`, { rumIds }),
      deleteSession: (id: string) => http.delete<void>(`/admin/sessions/${id}`),
      setMemberRole: (id: string, role: MemberRole) =>
        http.patch<MemberView>(`/admin/members/${id}/role`, { role }),
    },
  };
}

export type Api = ReturnType<typeof createApi>;

function toQuery(criteria: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(criteria)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}
